import { Container, Flex } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	type ShouldRevalidateFunctionArgs,
	useOutletContext,
} from '@remix-run/react'
import * as React from 'react'
import { z } from 'zod'

import { type loader } from './cart.server'
import { CartView } from '~/components/common/cart-view'
import { Header } from '~/routes/($lang).cart/header'
import { NoItems } from '~/routes/($lang).cart/no-items'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import {
	type Context,
	type FormEnum,
	ROOT_INPUTS,
} from '~/routes/_layout/root.component'
import { generateItemFormFetcherKey } from '~/utils/client/generate-item-form-fetcher-key.ts'
import { metaDefault } from '~/utils/client/meta-default.ts'
import { schemaDefault } from '~/utils/form-defaults.ts'
import { useCart } from '~/utils/hooks/use-cart.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeCart = 'route-cart',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).cart',
}

enum TEST_ID {
	cartCount = 'cart-count',
	cartEmpty = 'cart-empty',
	cartItems = 'cart-items',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	itemId = 'itemId',
	orderItemId = 'orderItemId',
	quantity = 'quantity',
}

/* -------------------------------------------------------------------------------------------------
 * Set quantity form
 * -----------------------------------------------------------------------------------------------*/

const FORM_SET_QUANTITY: FormEnum = {
	generateId: ({ id }) =>
		generateItemFormFetcherKey('cart-set-quantity-form', id),
	action: '/cart',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('set-quantity', id),
	subaction: 'set-quantity',
	redirect: '/cart',
}

const setQuantitySchema = z.object({
	...schemaDefault,
	...{
		[INPUTS.quantity]: z
			.number({
				invalid_type_error: 'validation.quantity.invalidType',
				required_error: 'validation.quantity.required',
			})
			.min(1, 'validation.quantity.min'),
    [INPUTS.orderItemId]: z
      .string(),
	},
})

/* -------------------------------------------------------------------------------------------------
 * Remove item form
 * -----------------------------------------------------------------------------------------------*/

const FORM_REMOVE_ITEM: FormEnum = {
	generateId: ({ id }) =>
		generateItemFormFetcherKey('cart-remove-item-form', id),
	action: '/cart',
	generateFetcherKey: ({ id }) => generateItemFormFetcherKey('remove-item', id),
	subaction: 'remove-item',
	redirect: '/cart',
}

const removeItemSchema = z.object({
	...schemaDefault,
  ...{
    [INPUTS.itemId]: z
      .string(),
  }
})

/* -------------------------------------------------------------------------------------------------
 * Schema
 * -----------------------------------------------------------------------------------------------*/

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.quantity]: z
			.number({
				invalid_type_error: 'validation.quantity.invalidType',
				required_error: 'validation.quantity.required',
			})
			.min(1, 'validation.quantity.min'),
	},
})

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<typeof loader> = (props: MetaArgs) =>
	metaDefault({ ...props, ROUTE_LOADER, I18NEXT })

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	defaultShouldRevalidate,
	formData,
}: ShouldRevalidateFunctionArgs) => {
	if (
		formData?.get(ROOT_INPUTS.subaction) === FORM_REMOVE_ITEM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction
	) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const { cart } = useOutletContext<Context>()

	const { optimisticItems, optimisticRemoved } = useCart({ cart })

	return (
		<Container px="5" mt="10em" mb="5em" className="app-Cart">
			<Flex direction="column" gapY="5">
				<Header />
				<Flex direction="column" align="center" p="1" flexGrow="1">
					{!!optimisticItems.length &&
					optimisticItems.length !== optimisticRemoved.length ? (
						<CartView cart={cart} />
					) : (
						<NoItems />
					)}
				</Flex>
			</Flex>
		</Container>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as CART_I18NEXT,
	ROUTE_LOADER as CART_ROUTE_LOADER,
	TEST_ID as CART_TEST_ID,
	INPUTS as CART_INPUTS,
	FORM_SET_QUANTITY as CART_FORM_SET_QUANTITY,
	FORM_REMOVE_ITEM as CART_FORM_REMOVE_ITEM,
	schema as cartSchema,
	setQuantitySchema as cartSetQuantitySchema,
	removeItemSchema as cartRemoveItemSchema,
}
